import { useCallback, useEffect, useMemo, useState } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import Env from 'src/environmentVariables/EnvironmentVariables';
import {
	BITCOIN_CHAIN_ID,
	SOLANA_CHAIN_ID,
	TRON_CHAIN_ID,
} from '@velocity-exchange/common';

// API Configuration
const FUN_XYZ_API_BASE_URL = 'https://api.fun.xyz';

const DRIFT_ACTION_TYPE = 'DRIFT_DEPOSIT';

// Target asset addresses on Solana for Fun.xyz
// These are the addresses Fun.xyz expects for the destination assets
// To add new assets: add an entry with the Fun.xyz target address
export const SOLANA_TARGET_ASSETS = {
	USDC: {
		address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
	},
	SOL: {
		address: '11111111111111111111111111111111',
	},
} as const;

export type SupportedTargetAsset = keyof typeof SOLANA_TARGET_ASSETS;

const SUPPORTED_TARGET_ASSETS = Object.keys(
	SOLANA_TARGET_ASSETS
) as SupportedTargetAsset[];

// Types
export interface FunXyzAsset {
	name: string;
	symbol: string;
	address: string;
	decimals: number;
	chainName: string;
	chainId: string;
	logo?: string;
	minCheckoutUsd?: number;
}

export interface FunXyzChain {
	chainId: string;
	chainName: string;
	nativeCurrency: string;
	logo?: string;
}

// Module-level caches keyed by targetAsset to avoid cross-asset collisions
const cachedAssetsMap = new Map<SupportedTargetAsset, FunXyzAsset[]>();
const cachePromiseMap = new Map<SupportedTargetAsset, Promise<FunXyzAsset[]>>();

// Module-level cache for deposit addresses (keyed by recipientAddress_subAccountId_targetAsset_sourceAsset)
const cachedDepositAddresses = new Map<string, CreateUdaResponse>();
const depositAddressPromiseMap = new Map<string, Promise<CreateUdaResponse>>();

function getDepositAddressCacheKey(
	recipientAddress: string,
	subAccountId: number,
	targetAsset: SupportedTargetAsset,
	sourceAsset?: FunXyzAsset
): string {
	return sourceAsset
		? `${recipientAddress}_${subAccountId}_${targetAsset}_${sourceAsset.chainId}_${sourceAsset.address}`
		: `${recipientAddress}_${subAccountId}_${targetAsset}`;
}

interface CreateUdaRequest {
	toChainId: string;
	toTokenAddress: string;
	recipientAddr: string;
	userId: string;
	actionType: string;
	clientMetadata?: {
		id: string;
		startTimestampMs: number;
		finalDollarValue: number;
		latestQuote: null;
		depositAddress: null;
		initSettings: {
			config: {
				targetAsset: string;
				targetChain: string;
				targetAssetTicker: string;
				checkoutItemTitle: string;
			};
		};
		selectedSourceAssetInfo: {
			address: string;
			symbol: string;
			chainId: string;
			iconSrc: null;
		};
		selectedPaymentMethodInfo: {
			paymentMethod: string;
			title: string;
			description: string;
		};
	};
}

export interface CreateUdaResponse {
	depositAddr?: string;
	solanaAddr?: string;
	tronAddr?: string;
	btcDepositAddr?: string;
	btcAddrSegwit?: string;
	btcAddrTaproot?: string;
}

export interface GetQuoteRequest {
	fromAmountBaseUnit: string;
	fromChainId: string;
	fromTokenAddress: string;
	recipientAddress: string;
	toChainId: string;
	toTokenAddress: string;
}

export interface GetQuoteResponse {
	quoteId: string;
	estToTokenBaseUnit: string;
	estInputUsd: number;
	estOutputUsd: number;
	estCheckoutTimeMs: number;
	estFeeBreakdown: {
		gasUsd: number;
		fillCostUsd: number;
		fillCostPercent: number;
		totalImpactUsd: number;
		totalImpact: number;
		swapImpact: number;
		swapImpactUsd: number;
		appFeePercent: number;
		appFeeUsd: number;
		maxSlippage: number;
		minReceived: number;
		appFeeLabel: string;
		exchangeRate: number;
	};
}

// API helper
async function funXyzFetch<T>(
	endpoint: string,
	options?: RequestInit
): Promise<T> {
	const apiKey = Env.funXyzApiKey;
	if (!apiKey) {
		throw new Error('Fun.xyz API key not configured');
	}

	const response = await fetch(`${FUN_XYZ_API_BASE_URL}${endpoint}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'x-api-key': apiKey,
			...options?.headers,
		},
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Fun.xyz API error: ${response.status} - ${errorText}`);
	}

	return response.json();
}

// API response type: nested object { chainId: { tokenAddress: asset } }
type SupportedAssetsResponse = Record<string, Record<string, FunXyzAsset>>;

// Cache for the full /v1/assets/supported (no params) response
let cachedAllAssets: SupportedAssetsResponse | null = null;
let allAssetsPromise: Promise<SupportedAssetsResponse> | null = null;

async function fetchAllSupportedAssets(): Promise<SupportedAssetsResponse> {
	if (cachedAllAssets) return cachedAllAssets;
	if (allAssetsPromise) return allAssetsPromise;

	allAssetsPromise = funXyzFetch<SupportedAssetsResponse>(
		'/v1/assets/supported'
	).then((response) => {
		cachedAllAssets = response;
		allAssetsPromise = null;
		return response;
	});

	return allAssetsPromise;
}

/**
 * Fetch all supported assets and split into:
 * - solanaSourceSymbols: Solana tokens (for filtering Drift spot markets)
 * - destinationAssets: non-Solana tokens (for withdrawal receiving list)
 */
export async function fetchWithdrawalSupportedAssets(): Promise<{
	solanaSourceSymbols: string[];
	destinationAssets: FunXyzAsset[];
}> {
	const response = await fetchAllSupportedAssets();

	const solanaAssets = response[SOLANA_CHAIN_ID];
	const solanaSourceSymbols = solanaAssets
		? Object.values(solanaAssets).map((a) => a.symbol.toUpperCase())
		: [];

	const destinationAssets: FunXyzAsset[] = [];
	for (const [, chainAssets] of Object.entries(response)) {
		for (const asset of Object.values(chainAssets)) {
			destinationAssets.push(asset);
		}
	}

	return { solanaSourceSymbols, destinationAssets };
}

// Fetch supported assets from Fun.xyz API
async function fetchSupportedAssets(
	targetAsset: SupportedTargetAsset
): Promise<FunXyzAsset[]> {
	const targetConfig = SOLANA_TARGET_ASSETS[targetAsset];
	if (!targetConfig) {
		throw new Error(`Unsupported target asset: ${targetAsset}`);
	}

	const params = new URLSearchParams({
		toChainId: SOLANA_CHAIN_ID,
		toTokenAddress: targetConfig.address,
	});

	const response = await funXyzFetch<SupportedAssetsResponse>(
		`/v1/assets/supported?${params.toString()}`
	);

	// Flatten the nested object structure into an array of assets
	const assets: FunXyzAsset[] = [];
	for (const chainAssets of Object.values(response)) {
		for (const asset of Object.values(chainAssets)) {
			assets.push(asset);
		}
	}

	return assets;
}

/**
 * Prefetch Fun.xyz supported assets (call before modal opens for faster loading)
 * This is a standalone function that populates the module-level cache
 */
export async function prefetchFunXyzAssets(
	targetAsset: SupportedTargetAsset = 'USDC'
): Promise<FunXyzAsset[]> {
	const cached = cachedAssetsMap.get(targetAsset);
	if (cached) return cached;

	const inFlight = cachePromiseMap.get(targetAsset);
	if (inFlight) return inFlight;

	try {
		const promise = fetchSupportedAssets(targetAsset);
		cachePromiseMap.set(targetAsset, promise);
		const assets = await promise;
		cachedAssetsMap.set(targetAsset, assets);
		return assets;
	} catch (error) {
		cachePromiseMap.delete(targetAsset);
		throw error;
	}
}

/**
 * Prefetch Fun.xyz deposit address for default selection (USDC on Ethereum)
 * Call this when wallet connects to have the address ready when modal opens
 */
export async function prefetchFunXyzDepositAddress(
	recipientAddress: string,
	subAccountId: number,
	targetAsset: SupportedTargetAsset = 'USDC'
): Promise<CreateUdaResponse | null> {
	const assets = await prefetchFunXyzAssets(targetAsset);
	const defaultAsset = assets.find(
		(a) => a.symbol === 'USDC' && a.chainId === '1'
	);
	if (!defaultAsset) return null;

	const cacheKey = getDepositAddressCacheKey(
		recipientAddress,
		subAccountId,
		targetAsset,
		defaultAsset
	);

	const cached = cachedDepositAddresses.get(cacheKey);
	if (cached) return cached;

	const inFlight = depositAddressPromiseMap.get(cacheKey);
	if (inFlight) return inFlight;

	try {
		const promise = createUda(
			recipientAddress,
			targetAsset,
			subAccountId,
			defaultAsset
		);
		depositAddressPromiseMap.set(cacheKey, promise);
		const result = await promise;
		cachedDepositAddresses.set(cacheKey, result);
		return result;
	} catch (error) {
		depositAddressPromiseMap.delete(cacheKey);
		throw error;
	} finally {
		depositAddressPromiseMap.delete(cacheKey);
	}
}

// Create UDA (deposit address) via Fun.xyz API
async function createUda(
	recipientAddress: string,
	targetAsset: SupportedTargetAsset,
	subAccountId: number,
	sourceAsset?: FunXyzAsset
): Promise<CreateUdaResponse> {
	const targetConfig = SOLANA_TARGET_ASSETS[targetAsset];
	if (!targetConfig) {
		throw new Error(`Unsupported target asset: ${targetAsset}`);
	}

	// For Drift, userId is an EVM-compatible address where the subAccountId is encoded
	// in the last digits (0 for new users or main account, 1+ for subaccounts)
	const userId = `0x${subAccountId.toString(16).padStart(40, '0')}`;

	const request: CreateUdaRequest = {
		toChainId: SOLANA_CHAIN_ID,
		toTokenAddress: targetConfig.address,
		recipientAddr: recipientAddress,
		userId,
		actionType: DRIFT_ACTION_TYPE,
		clientMetadata: {
			id: '',
			startTimestampMs: Date.now(),
			finalDollarValue: 0,
			latestQuote: null,
			depositAddress: null,
			initSettings: {
				config: {
					targetAsset: targetConfig.address,
					targetChain: SOLANA_CHAIN_ID,
					targetAssetTicker: targetAsset,
					checkoutItemTitle: `Deposit ${targetAsset} to Drift`,
				},
			},
			selectedSourceAssetInfo: {
				address: sourceAsset?.address ?? '0x',
				symbol: sourceAsset?.symbol ?? '',
				chainId: sourceAsset?.chainId ?? '',
				iconSrc: null,
			},
			selectedPaymentMethodInfo: {
				paymentMethod: 'token_transfer',
				title: 'QR Code Transfer',
				description: '',
			},
		},
	};

	return funXyzFetch<CreateUdaResponse>('/v1/eoa', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}

// --- Withdrawal ---

export interface CreateWithdrawalUdaParams {
	toChainId: string;
	toTokenAddress: string;
	toTokenTicker: string;
	recipientAddress: string;
	sourceTokenAddress: string;
	sourceTokenSymbol: string;
	subAccountId: number;
}

/**
 * Create a withdrawal UDA — reversed params from deposit.
 * For deposit: from external chain → to Solana
 * For withdrawal: from Solana → to external chain
 *
 * Returns a UDA response where `solanaAddr` is the Solana address
 * to send tokens to. Fun.xyz bridges them to the destination.
 */
export async function createWithdrawalUda({
	toChainId,
	toTokenAddress,
	toTokenTicker,
	recipientAddress,
	sourceTokenAddress,
	sourceTokenSymbol,
	subAccountId,
}: CreateWithdrawalUdaParams): Promise<CreateUdaResponse> {
	const userId = `0x${subAccountId.toString(16).padStart(40, '0')}`;

	const request = {
		toChainId,
		toTokenAddress,
		recipientAddr: recipientAddress,
		userId,
		clientMetadata: {
			id: '',
			startTimestampMs: Date.now(),
			finalDollarValue: 0,
			latestQuote: null,
			depositAddress: null,
			initSettings: {
				config: {
					targetAsset: toTokenAddress,
					targetChain: toChainId,
					targetAssetTicker: toTokenTicker,
					checkoutItemTitle: `Withdraw ${sourceTokenSymbol} from Drift`,
				},
			},
			selectedSourceAssetInfo: {
				address: sourceTokenAddress,
				symbol: sourceTokenSymbol,
				chainId: SOLANA_CHAIN_ID,
				iconSrc: null,
			},
			selectedPaymentMethodInfo: {
				paymentMethod: 'token_transfer',
				title: 'Drift Withdrawal',
				description: '',
			},
		},
	};

	return funXyzFetch<CreateUdaResponse>('/v1/eoa', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}

// Get quote from Fun.xyz API
async function getQuote(request: GetQuoteRequest): Promise<GetQuoteResponse> {
	return funXyzFetch<GetQuoteResponse>('/v1/eoa/quote', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}

interface UseFunXyzActionsParams {
	targetAsset?: SupportedTargetAsset;
	/** When provided, use this account for deposit address instead of active account. Prevents switching active subaccount. */
	targetAccountKey?: string | null;
	/** When true, generate the deposit address for the next (new) subaccount instead of any existing account. */
	forNewSubaccount?: boolean;
}

/**
 * Hook for Fun.xyz UDA (Universal Deposit Address) actions.
 *
 * Provides actions to:
 * - Fetch supported source assets for cross-chain deposits
 * - Create deposit addresses (UDAs) for receiving funds
 * - Get quotes for transfers
 */
export default function useFunXyzActions({
	targetAsset = 'USDC',
	targetAccountKey,
	forNewSubaccount = false,
}: UseFunXyzActionsParams = {}) {
	const { publicKey: walletPublicKey } = useCurrentWalletAdapter();

	// Use target account when provided (e.g. from DepositFromOtherSourcesModal). Otherwise use active account.
	const subAccountId = useDriftAccountStore((s) => {
		if (forNewSubaccount) {
			return;
		}

		const account = targetAccountKey
			? s.accounts[targetAccountKey]
			: s.accounts[s.currentUserKey ?? ''];

		return account?.userId;
	});

	const nextSubAccountId = useDriftStore(
		(s) => s.userStatsAccount?.numberOfSubAccountsCreated ?? 0
	);

	const subAccountIdToUse = subAccountId ?? nextSubAccountId;

	// Loading states
	const [isLoadingAssets, setIsLoadingAssets] = useState(false);
	const [isCreatingAddress, setIsCreatingAddress] = useState(false);
	const [isGettingQuote, setIsGettingQuote] = useState(false);

	// Data states
	const [supportedAssets, setSupportedAssets] = useState<FunXyzAsset[]>([]);
	const [depositAddresses, setDepositAddresses] =
		useState<CreateUdaResponse | null>(null);
	const [selectedSourceAsset, setSelectedSourceAsset] =
		useState<FunXyzAsset | null>(null);
	const [quote, setQuote] = useState<GetQuoteResponse | null>(null);

	// Error states
	const [assetsError, setAssetsError] = useState<Error | null>(null);
	const [createAddressError, setCreateAddressError] = useState<Error | null>(
		null
	);
	const [quoteError, setQuoteError] = useState<Error | null>(null);

	// Derived data: Group assets by chain
	const assetsByChain = useMemo(() => {
		const map = new Map<string, FunXyzAsset[]>();
		for (const asset of supportedAssets) {
			const chainKey = asset.chainId;
			const existing = map.get(chainKey) ?? [];
			map.set(chainKey, [...existing, asset]);
		}
		return map;
	}, [supportedAssets]);

	// Derived data: Unique chains
	// Extract chain logo from the native token (address 0xEeee...eeEE)
	const chains = useMemo(() => {
		const chainMap = new Map<string, FunXyzChain>();
		const NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

		for (const asset of supportedAssets) {
			const existing = chainMap.get(asset.chainId);
			const isNativeToken = asset.address === NATIVE_TOKEN_ADDRESS;

			if (!existing) {
				const displayName =
					asset.chainId === BITCOIN_CHAIN_ID
						? 'Bitcoin (Segwit)'
						: asset.chainName;
				chainMap.set(asset.chainId, {
					chainId: asset.chainId,
					chainName: displayName,
					nativeCurrency: asset.symbol,
					logo: isNativeToken ? asset.logo : undefined,
				});
			} else if (isNativeToken && !existing.logo) {
				// Update with native token logo if we find it later
				existing.logo = asset.logo;
			}
		}
		return Array.from(chainMap.values());
	}, [supportedAssets]);

	// Action: Fetch supported assets (uses cache if available)
	const loadSupportedAssets = useCallback(
		async (asset: SupportedTargetAsset = targetAsset) => {
			const cached = cachedAssetsMap.get(asset);
			if (cached) {
				setSupportedAssets(cached);
				return cached;
			}

			const inFlight = cachePromiseMap.get(asset);
			if (inFlight) {
				try {
					const assets = await inFlight;
					setSupportedAssets(assets);
					return assets;
				} catch (error) {
					const err =
						error instanceof Error ? error : new Error('Failed to load assets');
					setAssetsError(err);
					throw err;
				}
			}

			setIsLoadingAssets(true);
			setAssetsError(null);

			try {
				const promise = fetchSupportedAssets(asset);
				cachePromiseMap.set(asset, promise);
				const assets = await promise;
				cachedAssetsMap.set(asset, assets);
				setSupportedAssets(assets);
				return assets;
			} catch (error) {
				const err =
					error instanceof Error ? error : new Error('Failed to load assets');
				setAssetsError(err);
				cachePromiseMap.delete(asset);
				throw err;
			} finally {
				setIsLoadingAssets(false);
			}
		},
		[targetAsset]
	);

	// Initialize from cache on mount or when targetAsset changes
	useEffect(() => {
		const cached = cachedAssetsMap.get(targetAsset);
		if (cached) {
			setSupportedAssets(cached);
		}
	}, [targetAsset]);

	// Action: Get assets for a specific chain
	const getAssetsForChain = useCallback(
		(chainId: string): FunXyzAsset[] => {
			return assetsByChain.get(chainId) ?? [];
		},
		[assetsByChain]
	);

	// Action: Create deposit address (uses cache if available)
	const createDepositAddress = useCallback(
		async (asset: SupportedTargetAsset, sourceAsset?: FunXyzAsset) => {
			const recipientAddress = walletPublicKey?.toString();

			if (!recipientAddress) {
				throw new Error('Wallet not connected');
			}

			const cacheKey = getDepositAddressCacheKey(
				recipientAddress,
				subAccountIdToUse,
				asset,
				sourceAsset
			);

			const cached = cachedDepositAddresses.get(cacheKey);
			if (cached) {
				setDepositAddresses(cached);
				setSelectedSourceAsset(sourceAsset ?? null);
				return cached;
			}

			const inFlight = depositAddressPromiseMap.get(cacheKey);
			if (inFlight) {
				const response = await inFlight;
				setDepositAddresses(response);
				setSelectedSourceAsset(sourceAsset ?? null);
				return response;
			}

			setIsCreatingAddress(true);
			setCreateAddressError(null);

			try {
				const promise = createUda(
					recipientAddress,
					asset,
					subAccountIdToUse,
					sourceAsset
				);
				depositAddressPromiseMap.set(cacheKey, promise);
				const response = await promise;
				cachedDepositAddresses.set(cacheKey, response);
				setDepositAddresses(response);
				setSelectedSourceAsset(sourceAsset ?? null);
				return response;
			} catch (error) {
				const err =
					error instanceof Error
						? error
						: new Error('Failed to create deposit address');
				setCreateAddressError(err);
				throw err;
			} finally {
				depositAddressPromiseMap.delete(cacheKey);
				setIsCreatingAddress(false);
			}
		},
		[walletPublicKey, subAccountIdToUse]
	);

	// Action: Get quote for transfer
	const fetchQuote = useCallback(
		async (request: GetQuoteRequest, maxRetries = 0) => {
			setIsGettingQuote(true);
			setQuoteError(null);

			try {
				for (let attempt = 0; attempt <= maxRetries; attempt++) {
					try {
						const response = await getQuote(request);
						setQuote(response);
						return response;
					} catch (error) {
						if (attempt === maxRetries) throw error;
						await new Promise((r) => setTimeout(r, 1000));
					}
				}
			} catch (error) {
				const err =
					error instanceof Error ? error : new Error('Failed to get quote');
				setQuoteError(err);
				throw err;
			} finally {
				setIsGettingQuote(false);
			}
		},
		[]
	);

	// Get the receiving address from deposit addresses based on source chain
	// - Tron: tronAddr
	// - Solana: solanaAddr
	// - Bitcoin: btcAddrSegwit
	// - EVM chains (everything else): depositAddr
	const receivingAddress = useMemo(() => {
		if (!depositAddresses) return null;

		const sourceChainId = selectedSourceAsset?.chainId;

		if (sourceChainId === TRON_CHAIN_ID) {
			return depositAddresses.tronAddr ?? null;
		}
		if (sourceChainId === SOLANA_CHAIN_ID) {
			return depositAddresses.solanaAddr ?? null;
		}
		if (sourceChainId === BITCOIN_CHAIN_ID) {
			return depositAddresses.btcAddrSegwit ?? null;
		}

		// Default to EVM deposit address
		return depositAddresses.depositAddr ?? null;
	}, [depositAddresses, selectedSourceAsset]);

	return {
		// Supported assets
		supportedAssets,
		chains,
		assetsByChain,
		isLoadingAssets,
		assetsError,

		// Deposit address
		depositAddresses,
		receivingAddress,
		selectedSourceAsset,
		isCreatingAddress,
		createAddressError,

		// Quote
		quote,
		isGettingQuote,
		quoteError,

		// Actions
		loadSupportedAssets,
		getAssetsForChain,
		createDepositAddress,
		fetchQuote,
		clearQuote: useCallback(() => setQuote(null), []),

		// Config
		targetAsset,
		supportedTargetAssets: SUPPORTED_TARGET_ASSETS,
	};
}
